import '../styles/Person.css';
import React, {useState, useEffect, useRef, useLayoutEffect} from 'react';
import {useParams, Link} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {ImgZoom, Loading, InfoBox, useAuthUser} from '../components/components_index';
import {usePeople, useReunions, useImgDescs, useMD, getImgOptions} from '../hooks/hooks_index';

// const person_id = "P400000";

export default function Person({children}) {
  const {personID} = useParams();

  return (
    <Page person_id={personID} key={personID}>
      <Title />
      <InfoBox />
      <Bio />
      <Gallery />
    </Page>
  );
}

function Page({children, person_id}) {
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const {data: image_infos, isLoading: imgLoading, error: imgError} = useImgDescs();
  const { role } = useAuthUser();

  if (peopleLoading || imgLoading) return <Loading/>;
  if (peopleError || imgError) return <div>Error</div>;

  if (!people[person_id] || !people || !image_infos) return <Loading/>;
  const person = people[person_id];

  const childrenWithProps = React.Children.map(children, child =>
    React.cloneElement(child, {person, image_infos, person_id, people})
  );

  return (
    <div className='person-background'>
      {childrenWithProps}
      {(role === "admin") &&
        <Link to={`/edit-bio/${person_id}`}>
          Edit This Bio Page
        </Link>
      }
    </div>
  );
}

function Title({person}) {
  return (
    <div className='title-box'>
      <div className='title-text'>
        {person.AKA || person.f_name+' '+person.l_name}
      </div>
    </div>
  );
}

function Bio({person_id, image_infos}) {
  const {data: bioText, isLoading} = useMD("person", person_id)

  if (isLoading) return <Loading/>

  return (
    <div className='bio-box'>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => {
            return <div className="bio-image-paragraph">{children}</div>;
          },
          img: ({src, alt}) => {
            if (src === undefined || src === "" || !src || src === "%22undefined%22") {
              return <div className='bio-image-box'>Empty Image Box</div>
            } else {
              return (
              <div className='bio-image-box'>
                <ImgZoom src={src} alt={alt} className={'bio-image'} bioPic={true} />
                {image_infos[src]?.subtext &&
                  <div>{image_infos[src]?.subtext}</div>
                }
              </div>
            )}
          },
        }}
      >
        {bioText || ""}
      </ReactMarkdown>
    </div>
  );
}

function Gallery({person, image_infos, person_id, people}) {
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [atLeft, setAtLeft] = useState(true);
  const [atRight, setAtRight] = useState(false);
  
  const scrollAmount = 200;

  const scrollLeft = () => smoothScrollBy(scrollRef.current, -scrollAmount, 400)
  const scrollRight = () => smoothScrollBy(scrollRef.current, scrollAmount, 400)

  useEffect(() => {
    getImgOptions({
      people: [person_id],
      sortByDate: ["saved_date", "desc"]
    })
      .then(data => setImages(data))
      .catch(err => setError(err.message));
  }, [person_id]);

  // params.sortByDate = [
  //     sortField,
  //     sortDirection
  //   ];

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setShowArrows(el.scrollWidth > el.clientWidth);
    };
    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [images]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const overflowing = el.scrollWidth > el.clientWidth;

      setShowArrows(overflowing);

      setAtLeft(el.scrollLeft <= 0);

      setAtRight(
        el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
      );
    };

    updateScrollState();

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [images]);

  if (error) return <div>Error: {error}</div>

  return (
    <div className='person-gallery'>
      <h2>Gallery</h2>
      <div className='gallery-wrapper'>
        <div className='gallery-images-box' ref={scrollRef}>
          {images.map(img => (
            <div className='person-gallery-image-object' key={img.media_id}>
              <ImgZoom src={img.media_id} alt={img.subtext || img.img_name} className={'person-gallery-image'} img_array={images}/>
              {/* <div className="person-gallery-image-subtext">
                {img.subtext || img.img_name}
              </div> */}
            </div>
          ))}
          {images.length === 0 &&
            <div className='no-files'>No images in gallery</div>
          }
        </div>
        {showArrows && 
          <div>
            <button className={`scroll-button back-button ${atLeft?"disabled-scroll":""}`} onClick={scrollLeft}>
              {'\u2039'}
            </button>
            <button className={`scroll-button next-button ${atRight?"disabled-scroll":""}`} onClick={scrollRight}>
              {'\u203A'}
            </button>
          </div>
        }
      </div>
    </div>
  )
}

function smoothScrollBy(element, distance, duration = 600) {
  if (!element) return;

  const start = element.scrollLeft;
  const startTime = performance.now();

  function easeInOut(t) {
    return t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function animate(time) {
    const elapsed = time - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeInOut(progress);

    element.scrollLeft = start + distance * eased;

    if (progress < 1) requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);
}
